'use client'

import { createContext, useContext, useState, useEffect } from 'react'
import { useAuth } from './AuthContext'
import constants from '@/helpers/constants'
import { saveToStorage, getFromStorage } from '@/helpers/storage.helper'
import { encryptData, decryptData } from '@/helpers/crypto'

const BookingContext = createContext()
const keyMap = { p: 'place', e: 'event', a: 'activity' }

const CACHE_DURATION = 3 * 60 * 1000 // 3 minutes in ms

// In-memory cache for listings
const listingCache = {}

// Hydrate cache from localStorage on module load (client-side only)
if (typeof window !== 'undefined') {
  try {
    const persisted = localStorage.getItem('listingCache')
    if (persisted) {
      const parsed = JSON.parse(persisted)
      const now = Date.now()
      // Remove expired entries
      for (const key in parsed) {
        if (
          parsed[key]?.timestamp &&
          now - parsed[key].timestamp < CACHE_DURATION
        ) {
          listingCache[key] = parsed[key]
        }
      }
    }
  } catch {}
}

export function BookingProvider ({
  children,
  listingUUID: propListingUUID,
  listingMinType: propListingType
}) {
  const [listingUUID, setListingUUID] = useState(
    // () =>
    //   propListingUUID ||
    //   (typeof window !== 'undefined' && localStorage.getItem('listingUUID')) ||
    null
  )
  const [listingMinType, setListingMinType] = useState(
    // () =>
    //   propListingType ||
    //   (typeof window !== 'undefined' &&
    //     localStorage.getItem('listingMinType')) ||
    null
  )
  const [listingSettings, setListingSettings] = useState(null)
  const [bookingScheme, setBookingScheme] = useState(null)
  const [listing, setListing] = useState(null)

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [listingId, setListingId] = useState(null) // backend listing_id
  const listing_type = keyMap[listingMinType]

  // Add services and currency state
  const [services, setServices] = useState(null)
  const [currency, setCurrency] = useState('')
  const [currency_ar, setCurrencyAr] = useState('')
  const [selectedService, setSelectedService] = useState(null)

  const { logout } = useAuth()

  // Persist listingUUID and listingMinType to localStorage
  useEffect(() => {
    if (listingUUID) localStorage.setItem('listingUUID', listingUUID)
    if (listingMinType) localStorage.setItem('listingMinType', listingMinType)
  }, [listingUUID, listingMinType])

  // Persist cache to localStorage whenever it changes
  const persistCache = () => {
    try {
      // Only persist non-expired entries
      const now = Date.now()
      const filtered = {}
      for (const key in listingCache) {
        if (
          listingCache[key]?.timestamp &&
          now - listingCache[key].timestamp < CACHE_DURATION
        ) {
          filtered[key] = listingCache[key]
        }
      }
      localStorage.setItem('listingCache', JSON.stringify(filtered))
    } catch {}
  }

  // Fetch listing_id first, then fetch listing, listingSettings and bookingScheme using listing_id
  useEffect(() => {
    async function fetchAllBookingData () {
      setLoading(true)
      setError(null)
      setListingSettings(null)
      setBookingScheme(null)
      setListing(null)
      setListingId(null)
      try {
        // Use a composite key for cache
        const cacheKey = `${listingUUID || ''}_${listingMinType || ''}`
        const now = Date.now()
        const cached = listingCache[cacheKey]
        // if (
        //   listingUUID &&
        //   listingMinType &&
        //   cached &&
        //   cached.timestamp &&
        //   now - cached.timestamp < CACHE_DURATION
        // ) {
        //   setListing(cached.listing)
        //   setListingSettings(cached.listingSettings)
        //   setBookingScheme(cached.bookingScheme)
        //   setListingId(cached.listingId)
        //   setLoading(false)
        //   return
        // }

        // console.log('Fetching booking data for:', {
        //   listingUUID,
        //   listingMinType
        // })

        // Encrypt payload for /listing_id
        const idData = await retryAsync(
          () =>
            fetch(constants.BASE_URL + '/listing_id', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                encrypted: encryptData({
                  listing_uuid: listingUUID,
                  listing_type: keyMap[listingMinType]
                })
              })
            }),
          { decrypt: true }
        )
        if (idData?.error) {
          throw new Error(idData.error)
        }
        const backendListingId = idData
        setListingId(backendListingId)

        if (backendListingId) {
          // Fetch listing, settings, and scheme in parallel with retry
          const [itemData, settingsData, schemeData] = await Promise.all([
            retryAsync(
              () =>
                fetch(`${constants.BASE_URL}/listing/${listingMinType}`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    encrypted: encryptData({
                      listing_id: backendListingId
                    })
                  })
                }),
              { decrypt: true }
            ),
            retryAsync(
              () =>
                fetch(`${constants.BASE_URL}/listing-settings`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    encrypted: encryptData({
                      listing_id: backendListingId,
                      listing_type: keyMap[listingMinType]
                    })
                  })
                }),
              { decrypt: true }
            ),
            retryAsync(
              () =>
                fetch(constants.BASE_URL + '/listing-booking-scheme', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    encrypted: encryptData({
                      listing_id: backendListingId,
                      listing_type: keyMap[listingMinType]
                    })
                  })
                }),
              { decrypt: true }
            )
          ])
          if (itemData.code !== 200) {
            throw new Error(itemData.message || 'Failed to fetch listing data')
          } else if (itemData.message == 'This listing does not exist.') {
            throw new Error('This listing does not exist.')
          }
          setListing(itemData.data.listing[0])
          setListingSettings(settingsData.data)
          setBookingScheme(schemeData.data)

          // Cache the fetched data with timestamp
          listingCache[cacheKey] = {
            listing: itemData.data.listing[0],
            listingSettings: settingsData.data,
            bookingScheme: schemeData.data,
            listingId: backendListingId,
            timestamp: Date.now()
          }
          persistCache()
        }
      } catch (e) {
        console.log('Error fetching booking data:', e)
        setError(e.message ?? 'Failed to fetch booking data')
      }
      setTimeout(() => {
        setLoading(false)
      }, 1000)
    }
    if (listingUUID && listingMinType) {
      fetchAllBookingData()
    }
  }, [listingUUID, listingMinType])

  // Add getListingServices function
  const getListingServices = async (
    listingIdOverride,
    listingMinTypeOverride
  ) => {
    setServices(null)
    const id = listingIdOverride || listingId
    const type = listingMinTypeOverride || listingMinType

    if (!id || !type) return []
    try {
      const data = await retryAsync(
        () =>
          fetch(constants.BASE_URL + '/listing-services', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              encrypted: encryptData({
                listing_id: id,
                listing_type: keyMap[type]
              })
            })
          }),
        { decrypt: true }
      )

      if (data?.message == 'Unauthorized') {
        console.log('Unauthorized', data)
        logout()
        setTimeout(() => {
          setServices(null)
          setCurrency('')
          setCurrencyAr('')
        }, 300)
        return
      }
      if (data?.data?.code === 200) {
        const servicesArr = data.data?.data?.filter_booking_services || []
        setServices(servicesArr)
        setCurrency(data?.data?.data?.currency?.trim() || '')
        setCurrencyAr(data?.data?.data?.currency_ar.trim() || '')
        return {
          services: servicesArr,
          currency: data?.data?.currency,
          currency_ar: data?.data?.currency_ar
        }
      }

      throw new Error(
        `Failed to fetch services: ${
          data.message || data?.data || 'Unknown error'
        }`
      )
    } catch (e) {
      setError(e.message || 'Failed to fetch services')
      setServices(null)
      setCurrency('')
      setCurrencyAr('')
      console.log('Failed to fetch services:', e)
      return { services: [], currency: '', currency_ar: '' }
    }
  }

  const [bookingResult, setBookingResultState] = useState(() => {
    return getFromStorage('bookingResult') || null
  })

  const setBookingResult = data => {
    setBookingResultState(data)
    saveToStorage('bookingResult', data)
  }

  useEffect(() => {
    const handleStorage = e => {
      if (e.key === 'bookingResult') {
        setBookingResultState(e.newValue ? JSON.parse(e.newValue) : null)
      }
    }
    window.addEventListener('storage', handleStorage)
    return () => window.removeEventListener('storage', handleStorage)
  }, [])

  return (
    <BookingContext.Provider
      value={{
        listingSettings,
        bookingScheme,
        listing,
        loading,
        error,
        listingUUID,
        setListingUUID,
        setListingMinType,
        listingId,
        listingMinType,
        getListingServices,
        services,
        currency,
        currency_ar,
        selectedService,
        setSelectedService,
        listing_type,
        bookingResult,
        setBookingResult
      }}
    >
      {children}
    </BookingContext.Provider>
  )
}

export function useBooking () {
  return useContext(BookingContext)
}

async function retryAsync (
  fn,
  { retries = 3, delay = 300, decrypt = false } = {}
) {
  let lastError
  for (let i = 0; i < retries; i++) {
    try {
      const res = await fn()
      if (!res.ok) throw new Error(`HTTP error: ${res.status}`)
      const json = await res.json()
      const data = decrypt ? decryptData(json.encrypted) : json
      if (
        data.code !== 200 &&
        data?.data?.code != 200 &&
        typeof data !== 'number'
      ) {
        throw new Error(data.message || 'Failed to fetch data')
      }
      return data
    } catch (e) {
      lastError = e
      if (i < retries - 1) await new Promise(res => setTimeout(res, delay))
    }
  }
  throw lastError
}
